import { MouseEvent, SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useMedia } from 'hooks/useMedia'
import { renderModules } from '../../lib/renderModules'
import { TabmoduleContent } from './TabNavi.content'
import { animated, useSpring } from 'react-spring'
import { Accordion as AccordionReach } from '@reach/accordion'
import { CustomAccordionItem } from 'components/Accordion/CustomAccordionItem'
import '@reach/accordion/styles.css'

type Props = { data: TabmoduleContent[] }

export const TabNaviMobile = ({ data }: Props) => {
  return (
    <div>
      <AccordionReach collapsible multiple className="">
        {data?.map((tab) => (
          <CustomAccordionItem
            itemHeading={tab.data.headline}
            key={tab.data.headline}
            className="tabnavi-accordion"
          >
            {renderModules(tab)}
          </CustomAccordionItem>
        ))}
      </AccordionReach>
    </div>
  )
}

export const TabNaviDesktop = ({ data }: Props) => {
  const [shownTab, setShownTab] = useState(data[0]?.data.headline)
  const tabs = useMemo(() => data.map((item) => item.data.headline), [data])
  const [styles, api] = useSpring(() => ({ x: 0, width: 0 }))
  const ref = useRef<HTMLButtonElement>(null)
  const wrapperRef = useRef<HTMLUListElement>(null)

  useEffect(() => {
    const { width, x } = ref.current.getBoundingClientRect()
    const { scrollLeft } = wrapperRef.current
    const { x: wrapperX } = wrapperRef.current.getBoundingClientRect()

    api.start({ width, x: x - wrapperX + scrollLeft })
  }, [api])

  const tabInteraction = (event?: SyntheticEvent | MouseEvent, tabname?: string) => {
    let width = 0
    let x = 0

    if (typeof event !== 'undefined' && (event.type === 'mouseover' || event.type === 'click')) {
      x = event.currentTarget.getBoundingClientRect().x
      width = event.currentTarget.getBoundingClientRect().width
    } else {
      x = ref.current.getBoundingClientRect().x
      width = ref.current.getBoundingClientRect().width
    }
    const { scrollLeft } = wrapperRef.current
    const { x: wrapperX } = wrapperRef.current.getBoundingClientRect()

    api.start({ width, x: x - wrapperX + scrollLeft })

    if (typeof tabname !== 'undefined') {
      setShownTab(tabname)
    }
  }

  const [tabBarHovered, setTabBarHovered] = useState(false)

  const disableScrolling = () => {
    document.body.style.overflow = 'hidden'
  }

  const enableScrolling = () => {
    document.body.style.overflow = 'unset'
  }

  const tabScroll = (e) => {
    const el = wrapperRef.current

    const tabBarScrolling =
      !(el.scrollLeft === 0 && e.deltaY < 0) &&
      !(el.scrollWidth - el.clientWidth - Math.round(el.scrollLeft) === 0 && e.deltaY > 0)

    if (tabBarScrolling && tabBarHovered) {
      disableScrolling()
      el.scrollLeft += e.deltaY
    } else {
      enableScrolling()
    }
  }

  return (
    <div>
      <div
        className="max-w-full bg-gray-light"
        onWheel={tabScroll}
        onMouseLeave={() => {
          setTabBarHovered(false)
          enableScrolling()
        }}
        onMouseEnter={() => setTabBarHovered(true)}
      >
        <ul
          className="overflow-tabs relative flex w-auto overflow-auto pl-20"
          onMouseLeave={tabInteraction}
          ref={wrapperRef}
        >
          <div className="flex space-x-14">
            {tabs.map((tabname) => (
              <li key={tabname}>
                <button
                  onClick={(event) => tabInteraction(event, tabname)}
                  className={`whitespace-nowrap py-4 text-xl leading-5 ${
                    tabname === shownTab ? 'font-bold' : ''
                  }`}
                  ref={tabname === shownTab ? ref : null}
                  onMouseOver={tabInteraction}
                >
                  {tabname}
                </button>
              </li>
            ))}
          </div>
          <div className="w-20 flex-shrink-0 bg-transparent" />
          <animated.div style={styles} className="absolute left-0 bottom-0 h-[2px] bg-black" />
        </ul>
      </div>
      {data?.map((tab) => (
        <div key={tab.data.headline} className={tab.data.headline === shownTab ? 'max-w-screen-xl' : 'sr-only'}>
          {renderModules(tab)}
        </div>
      ))}
    </div>
  )
}

export const TabNavi = ({ data }: Props) => {
  const isDesktop = useMedia('lg')

  return isDesktop ? <TabNaviDesktop data={data} /> : <TabNaviMobile data={data} />
}
